import React, { useEffect, useState } from "react";
import {
  CardContent,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Environment from "../Environment";
import { useAuth0 } from "@auth0/auth0-react";
import { APIRequest } from "../APIRequest";

const convertTimeStampToDate = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString() + " at " + date.toLocaleTimeString(navigator.language);
};

type UtidWithPo = {
  utid: string;
  poid: string;
};

interface SignaturesProps {
  requests: any,
  signaturesMode: SignaturesMode,
  singleUtid: string
}

export enum SignaturesMode {
  Selection,
  Single
}

export const Signatures: React.FC<SignaturesProps> = ({requests, signaturesMode, singleUtid}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [utidsWithPo, setUtidsWithPo] = useState<UtidWithPo[]>([]);
  const [selectedUtid, setSelectedUtid] = useState<string | "">("");
  const [signatures, setSignatures] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshSignatures = () => {
    if (!selectedUtid) return;

    setLoading(true);   
    setError(null);        

    requests.signaturesRequest
      .setEndpointParam("orderId", selectedUtid)
      .send()
      .onSuccess((result:any) => {
        setLoading(false);

        const sortedSignatures = result.signatures.map((signature: any) => ({
            type: signature.type,
            expiration: signature.exp,
            issued: signature.iat
          }))
          .sort((a: any, b: any) => a.issued - b.issued);

        setSignatures(sortedSignatures);        
      })
      .onError((context:any, status:string) => {
        setError(status);
        setLoading(false);
        setSignatures([]);        
      });
  }

  useEffect(() => {
    if(signaturesMode == SignaturesMode.Single) return;

    requests.userOrdersRequest.send()
      .onSuccess((orders:any) => {
        setUtidsWithPo(orders);
      })
      .onError((context:any, status:string) => console.log(status));
  }, [getAccessTokenSilently]);

  useEffect(() => {
    refreshSignatures();
  }, [selectedUtid, getAccessTokenSilently]);

  useEffect(() => {
    setSelectedUtid(singleUtid);
    refreshSignatures();
  }, [singleUtid]);

  return (
    <CardContent>
      {signaturesMode === SignaturesMode.Selection &&
        <FormControl fullWidth margin="normal">
          <InputLabel id="utid-select-label">Select Order</InputLabel>
          <Select
            labelId="utid-select-label"
            value={selectedUtid}
            label="Select Order"
            onChange={(e) => setSelectedUtid(e.target.value)}
          >
            {utidsWithPo.map(({ utid, poid }) => (
              <MenuItem key={utid} value={utid}>
                {poid} ({utid})
              </MenuItem>
            ))}
          </Select>
        </FormControl>}

      {loading && <Typography>Loading signature(s)...</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      {signatures.length > 0 && !loading && (
        <>
          <Typography variant="h6" gutterBottom>
            Signatures
          </Typography>
          <List>
            {signatures.map((sig, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={sig.type}
                  secondary={
                    <div>
                      <div>Issued: {convertTimeStampToDate(sig.issued)}</div>
                      <div>Expires: {convertTimeStampToDate(sig.expiration)}</div>
                    </div>}/>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </CardContent>
  );
};